import { useEffect, useMemo, useState } from 'react';

import { type Theme, ThemeProviderContext } from './ThemeProviderContext';

interface Props {
  children: React.ReactNode;
  storageKey?: string;
}

export function ThemeProvider({
  children,
  storageKey = 'vite-ui-theme',
  ...props
}: Props) {
  // Force light mode always
  const [theme] = useState<Theme>('light');

  useEffect(() => {
    const root = window.document.documentElement;

    // Force light mode
    root.classList.remove('dark');
    root.classList.add('light');
    document.documentElement.style.setProperty('color-scheme', 'light');
    localStorage.setItem(storageKey, 'light');

    // Watch for any attempts to add dark mode and prevent them
    const observer = new MutationObserver(() => {
      if (root.classList.contains('dark')) {
        root.classList.remove('dark');
        root.classList.add('light');
      }
    });

    observer.observe(root, {
      attributeFilter: ['class'],
      attributes: true
    });

    return () => {
      observer.disconnect();
    };
  }, [storageKey]);

  const value = useMemo(
    () => ({
      setTheme: () => {
        // Theme is locked to light mode
      },
      theme
    }),
    [theme]
  );

  return (
    <ThemeProviderContext
      {...props}
      value={value}
    >
      {children}
    </ThemeProviderContext>
  );
}
